// angular
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
// services
import { CookieService } from 'ngx-cookie-service';
// environment
import { environment } from '@environments/environment';
import { isPlatformBrowser } from '@angular/common';

@Injectable()
export class StorageService {
  // dependencies
  private readonly _cookieService = inject(CookieService);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {}

  setToken(token: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    this._cookieService.set(
      'token',
      token,
      expired,
      '/',
      window.location.hostname === 'localhost'
        ? 'localhost'
        : environment.domain
    );
  }

  setRefreshToken(refreshToken: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    this._cookieService.set(
      'refresh_token',
      refreshToken,
      expired,
      '/',
      window.location.hostname === 'localhost'
        ? 'localhost'
        : environment.domain
    );
  }

  setCurrency(currency: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.set(
        'currency',
        currency,
        expired,
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  getCurrency() {
    return this._cookieService.get('currency');
  }

  setLang(lang: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.set(
        'lang',
        lang,
        expired,
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  getLang() {
    return this._cookieService.get('lang');
  }

  setChatLang(lang: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.set(
        'chat-lang',
        lang,
        expired,
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  getChatLang() {
    return this._cookieService.get('chat-lang');
  }

  setChatId(chatId: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.set(
        'chat-id',
        chatId,
        expired,
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  getChatId() {
    return this._cookieService.get('chat-id');
  }

  setMemberId(memberId: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.set(
        'member-id',
        memberId,
        expired,
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  getMemberId() {
    return this._cookieService.get('member-id');
  }

  setStartedSupportChat() {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.set(
        'startedSupportChat',
        'true',
        expired,
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  getStartedSupportChat() {
    return this._cookieService.get('startedSupportChat');
  }

  getToken() {
    return this._cookieService.get('token');
  }

  getRefreshToken() {
    return this._cookieService.get('refresh_token');
  }

  setFirebaseToken(token: string) {
    const expired = new Date();
    expired.setMonth(new Date().getMonth() + 1);

    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.set(
        'firebaseToken',
        token,
        expired,
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  getFirebaseToken(): string {
    const token = this._cookieService.get('firebaseToken');
    return token ? token : '';
  }

  removeToken() {
    if (isPlatformBrowser(this._platformId)) {
      this._cookieService.delete(
        'token',
        '/',
        window.location.hostname === 'localhost'
          ? 'localhost'
          : environment.domain
      );
    }
  }

  removeRefreshToken() {
    this._cookieService.delete(
      'refresh_token',
      '/',
      window.location.hostname === 'localhost'
        ? 'localhost'
        : environment.domain
    );
  }

  removeFirebaseToken() {
    this._cookieService.delete(
      'firebase',
      '/',
      window.location.hostname === 'localhost'
        ? 'localhost'
        : environment.domain
    );
  }

  removeItemFromStorage(itemKey: string) {
    this._cookieService.delete(
      itemKey,
      '/',
      window.location.hostname === 'localhost'
        ? 'localhost'
        : environment.domain
    );
  }

  setReCaptchaToken(token: string) {
    const cookieExpiration = new Date();
    cookieExpiration.setMinutes(cookieExpiration.getMinutes() + 2);

    this._cookieService.set(
      'recaptcha_token',
      JSON.stringify(token),
      cookieExpiration,
      '/',
      window.location.hostname === 'localhost'
        ? 'localhost'
        : environment.domain,
      true, // secure flag for HTTPS
      'Strict'
    );
  }

  getReCaptchaToken() {
    const token = this._cookieService.get('recaptcha_token');
    if (token) {
      try {
        return JSON.parse(token);
      } catch (error) {
        return null;
      }
    }
    return null;
  }
}
